'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, CheckSquare, Printer, ShieldAlert, Truck } from 'lucide-react';

const steps = [
  {
    num: '01',
    title: 'Requirement & Material',
    desc: 'Detailed discussion to select the perfect face stock, adhesive type, and finishing features for your application.',
    icon: MessageSquare
  },
  {
    num: '02',
    title: 'Artwork Review & Approval',
    desc: 'Comprehensive check of registration, text clarity, ink coverage, and barcodes before creating printing plates.',
    icon: CheckSquare
  },
  {
    num: '03',
    title: 'Printing & Finishing',
    desc: 'Precision manufacturing on multi-color flexographic presses with inline UV curing and premium enhancements.',
    icon: Printer
  },
  {
    num: '04',
    title: 'Inspection & QC Testing',
    desc: 'Multi-stage quality checks including spectrophotometer matches and high-speed automated video inspection.',
    icon: ShieldAlert
  },
  {
    num: '05',
    title: 'Packaging & Dispatch',
    desc: 'Secure moisture-resistant packing, labeling of roll boxes, and prompt dispatch to support your timeline.',
    icon: Truck
  }
];

const AboutProcess = () => {
  return (
    <section className="w-full bg-white py-16 md:py-24" aria-labelledby="process-heading">
      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 md:px-8">
        <header className="mb-12 md:mb-16 text-center max-w-[800px] mx-auto">
          <span className="text-blue-600 font-bold tracking-[3px] uppercase text-xs mb-3 block">Operational Workflow</span>
          <h2
            id="process-heading"
            className="text-[#0F172A] font-extrabold tracking-tight text-[clamp(28px,4vw,38px)] leading-[1.2]"
          >
            Our Label Production Process
          </h2>
          <p className="mt-4 text-slate-600 text-[16px] md:text-[17px] leading-relaxed">
            From technical consultation to secure delivery, we coordinate each production stage carefully to guarantee consistent, application-ready results.
          </p>
        </header>

        {/* Desktop timeline horizontal, mobile vertical */}
        <div className="relative mt-8">
          {/* Connecting Line - Desktop Only */}
          <div className="hidden lg:block absolute top-[60px] left-[5%] right-[5%] h-0.5 bg-slate-200 z-0" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-6 relative z-10">
            {steps.map((step, idx) => (
              <motion.article
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                className="flex flex-col items-center text-center lg:items-start lg:text-left group"
              >
                {/* Step circle */}
                <div className="relative flex items-center justify-center mb-6">
                  {/* Step number badge */}
                  <span className="absolute -top-3 -right-3 text-[11px] font-bold tracking-widest text-[#C9A84C] bg-[#0F172A] px-2 py-0.5 rounded-full border border-slate-700 shadow-md">
                    {step.num}
                  </span>
                  
                  <div
                    className="h-16 w-16 rounded-2xl flex items-center justify-center text-[#C9A84C] group-hover:text-white transition-all duration-300 shadow-[0_4px_12px_rgba(201,168,76,0.08)]"
                    style={{
                      background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                      border: '1.5px solid rgba(201,168,76,0.3)',
                    }}
                  >
                    <step.icon className="h-6 w-6 group-hover:scale-110 transition-transform duration-300" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex flex-col gap-2 px-4 lg:px-0">
                  <h3 className="text-slate-900 font-bold text-[17px] leading-[1.3] group-hover:text-blue-600 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-slate-500 text-[14px] leading-relaxed max-w-[280px]">
                    {step.desc}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutProcess;
